import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  RunDetail,
  RunStep,
  ServerEvent,
  TranscriptSegment,
  TriggerObservation,
} from '@sfpbxtestbot/contracts';
import { api, type LogEntry } from './api.js';

export interface RunStreamState {
  run: RunDetail | undefined;
  logs: LogEntry[];
  transcript: TranscriptSegment[];
  triggers: TriggerObservation[];
  connected: boolean;
  loading: boolean;
  error: string | undefined;
  refresh: () => void;
}

const MAX_LOG_LINES = 3_000;

/**
 * Keeps one run's state current.
 *
 * The initial state comes from REST so a page load always shows the full
 * history, then the WebSocket applies deltas. If the socket drops, the hook
 * reconnects with backoff and refetches, which means a brief network blip costs
 * the operator nothing.
 */
export function useRunStream(runId: string | undefined): RunStreamState {
  const [run, setRun] = useState<RunDetail>();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [triggers, setTriggers] = useState<TriggerObservation[]>([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(Boolean(runId));
  const [error, setError] = useState<string>();
  const [reloadToken, setReloadToken] = useState(0);

  const socketRef = useRef<WebSocket | undefined>(undefined);
  const retryRef = useRef(0);
  const logCursor = useRef(0);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!runId) {
      setRun(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    logCursor.current = 0;

    void (async () => {
      try {
        const [detail, logResponse, call] = await Promise.all([
          api.run(runId),
          api.runLogs(runId),
          api.runCall(runId),
        ]);
        if (cancelled) return;
        setRun(detail);
        setLogs(logResponse.logs);
        logCursor.current = logResponse.logs.at(-1)?.id ?? 0;
        setTranscript(call.transcript);
        setTriggers(call.triggers);
        setError(undefined);
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load the run.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId, reloadToken]);

  useEffect(() => {
    if (!runId) return;

    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = (): void => {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const socket = new WebSocket(`${protocol}://${window.location.host}/ws`);
      socketRef.current = socket;

      socket.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        socket.send(JSON.stringify({ type: 'subscribe', runId }));
      };

      socket.onmessage = (message) => {
        let event: ServerEvent;
        try {
          event = JSON.parse(String(message.data)) as ServerEvent;
        } catch {
          return;
        }
        applyEvent(event);
      };

      socket.onclose = () => {
        setConnected(false);
        if (closed) return;
        // Back off, but keep the ceiling low: a run in progress is being watched.
        const delay = Math.min(8_000, 500 * 2 ** retryRef.current);
        retryRef.current += 1;
        reconnectTimer = setTimeout(() => {
          refresh();
          connect();
        }, delay);
      };

      socket.onerror = () => socket.close();
    };

    const applyEvent = (event: ServerEvent): void => {
      switch (event.type) {
        case 'run.status':
          setRun((current) =>
            current && current.id === event.runId
              ? { ...current, outcome: event.outcome, reason: event.reason ?? current.reason }
              : current,
          );
          break;

        case 'step.status':
          setRun((current) => (current ? { ...current, steps: mergeStep(current.steps, event.step) } : current));
          break;

        case 'assertion':
          setRun((current) => {
            if (!current) return current;
            return {
              ...current,
              steps: current.steps.map((step) =>
                step.stepKey === event.stepKey
                  ? {
                      ...step,
                      assertions: [
                        ...step.assertions.filter((item) => item.id !== event.assertion.id),
                        event.assertion,
                      ],
                    }
                  : step,
              ),
            };
          });
          break;

        case 'evidence':
          setRun((current) => {
            if (!current || !event.stepKey) return current;
            return {
              ...current,
              steps: current.steps.map((step) =>
                step.stepKey === event.stepKey
                  ? {
                      ...step,
                      evidence: [
                        ...step.evidence.filter((item) => item.id !== event.evidence.id),
                        event.evidence,
                      ],
                    }
                  : step,
              ),
            };
          });
          break;

        case 'log':
          setLogs((current) => {
            const next = [
              ...current,
              {
                id: (current.at(-1)?.id ?? 0) + 1,
                level: event.level,
                message: event.message,
                stepKey: event.stepKey,
                at: event.at,
              },
            ];
            return next.length > MAX_LOG_LINES ? next.slice(-MAX_LOG_LINES) : next;
          });
          break;

        case 'transcript':
          setTranscript((current) => [
            ...current,
            { speaker: event.speaker, text: event.text, at: event.at },
          ]);
          break;

        case 'trigger':
          setTriggers((current) => {
            const observation: TriggerObservation = {
              triggerId: event.triggerId,
              phrase: event.phrase,
              kind: event.kind,
              spokenAt: event.spokenAt,
              observedAt: event.observedAt,
              observed: event.observed,
              outcome: event.observed ? 'passed' : 'failed',
            };
            const rest = current.filter((item) => item.triggerId !== event.triggerId);
            return [...rest, observation];
          });
          break;

        case 'run.snapshot':
          setRun((current) =>
            current ? { ...current, ...event.run, steps: event.steps } : current,
          );
          break;

        case 'error':
          setError(event.message);
          break;
      }
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socketRef.current?.close();
      socketRef.current = undefined;
    };
  }, [runId, refresh]);

  return { run, logs, transcript, triggers, connected, loading, error, refresh };
}

function mergeStep(steps: RunStep[], incoming: RunStep): RunStep[] {
  const index = steps.findIndex((step) => step.stepKey === incoming.stepKey);
  if (index === -1) {
    return [...steps, incoming].sort((a, b) => a.position - b.position);
  }
  const next = [...steps];
  // Keep assertions and evidence already streamed in; the step message does not
  // resend them and dropping them would make the timeline flicker.
  const existing = next[index]!;
  next[index] = {
    ...incoming,
    assertions: incoming.assertions.length > 0 ? incoming.assertions : existing.assertions,
    evidence: incoming.evidence.length > 0 ? incoming.evidence : existing.evidence,
  };
  return next;
}
